import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function NoteSparkTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Note Spark: AI Note Taken"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Note
        Spark: AI Note Taken” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App is an AI-powered voice note tool that allows you to record
        spoken audio (for example, lectures, meetings, or personal dictation),
        transcribe recordings into text, automatically generate summaries, and
        export notes in formats such as PDF or TXT. The App is intended to help
        you capture and organize information, but it does not guarantee perfect
        accuracy or completeness. The App does not provide legal, medical, or
        other professional advice and does not replace taking your own notes or
        consulting qualified professionals.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Recording and Consent</div>
      <p>
        The App can record audio through your device’s microphone. You are
        solely responsible for how and when you choose to record. Before
        recording lectures, meetings, or conversations, you must comply with all
        applicable laws and rules, including any requirements to obtain consent
        from participants or to respect institutional policies (for example,
        classroom or workplace guidelines). You must not use the App to record
        people in situations where they have a reasonable expectation of
        privacy, or where recording is prohibited.
      </p>

      <div className={s.title}>5) Transcription and AI Summaries</div>
      <p>
        The App may use automated speech recognition and language models to
        convert your audio into text and to generate summaries, bullet points,
        or similar processed content. These features are provided on an “as is”
        basis and may include errors, omissions, or misinterpretations due to
        background noise, accents, technical limitations, or model behavior.
        Summaries are generated from the transcribed text and are not a complete
        or authoritative representation of the original content.
      </p>
      <p>
        AI-generated text in the App is produced automatically and may be
        incomplete, inaccurate, or not suitable for your specific context. You
        are responsible for reviewing and verifying all transcriptions and
        summaries before relying on them, sharing them, or using them for any
        decisions, including academic, professional, or personal decisions.
      </p>

      <div className={s.title}>6) Storage, History, and Exports</div>
      <p>
        The App may store recordings, transcripts, and summaries locally on your
        device and/or in cloud storage managed via third-party services. In-app
        lists of notes (for example, a history of past recordings) may reference
        local file paths or cloud IDs. Deleting the App or clearing data may
        permanently remove access to your stored notes, transcripts, and
        exports. You are responsible for any additional backups or exports you
        need.
      </p>
      <p>
        Export features (for example, PDF or TXT export) are provided for your
        convenience and may depend on device capabilities and third-party
        systems. We do not guarantee that exported files will be compatible with
        all software, viewers, or services.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        The App may offer auto-renewable subscriptions or other in-app products
        that unlock premium features (“Subscriptions”), such as higher usage
        limits, priority processing, or additional export options. A primary
        plan may include a 7-day free trial followed by a weekly fee (for
        example, $4.99 per week), and additional plans may offer monthly or
        yearly billing at different prices. Exact pricing, trial details, and
        names displayed in the store may vary by region and over time.
      </p>
      <p>
        Purchases are processed by Apple or Google through your store account;
        we do not receive your payment card numbers. If a free trial is offered,
        it will convert into a paid Subscription unless cancelled at least 24
        hours before the end of the trial period in your account settings. You
        can manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Usage Limits and Fair Use</div>
      <p>
        To maintain service quality and prevent abuse, the App may apply
        technical or policy-based limits on recording length, number of
        transcriptions or summaries per period, export volume, or similar
        metrics. Premium plans may increase these limits, but they remain
        subject to reasonable fair-use thresholds. We may adjust such limits
        over time, publish them in-app, or enforce them through warnings,
        slower processing, or temporary restrictions where necessary to protect
        the service.
      </p>

      <div className={s.title}>9) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal purposes and not to:
      </p>
      <ul>
        <li>
          record or transcribe audio in violation of privacy, wiretapping, or
          other applicable laws;
        </li>
        <li>
          upload or process content that is illegal, abusive, hateful, or
          infringes the rights of others, including intellectual property and
          personality rights;
        </li>
        <li>
          use the App to create misleading transcripts or summaries with the
          intent to defraud, manipulate, or harm others;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          use automated scripts, bots, or other means to overload, scrape, or
          misuse the App or its infrastructure.
        </li>
      </ul>

      <div className={s.title}>10) Intellectual Property</div>
      <p>
        You retain ownership of your original recordings and the content you
        provide, subject to the license granted below. All software, models,
        interfaces, designs, trademarks, and other materials provided with the
        App (excluding your content) are owned by the publisher or its licensors
        and are protected by intellectual property laws. Subject to these Terms
        and any Subscription limitations, you are granted a limited,
        non-exclusive, non-transferable, revocable license to use the App and
        its features solely for your personal note-taking and transcription
        needs.
      </p>
      <p>
        By using the App, you grant us and our service providers a worldwide,
        non-exclusive, royalty-free license to process your content as needed to
        provide and improve the App&apos;s features, including recording,
        transcription, summarization, storage, and export.
      </p>

      <div className={s.title}>11) Third-Party Services and AI Providers</div>
      <p>
        The App may rely on third-party services for storage, analytics, error
        reporting, speech-to-text processing, and AI-based summarization or text
        generation. Examples include cloud infrastructure, speech recognition
        APIs, and AI model providers. When you use features such as
        transcription or summarization, audio and text data may be transmitted
        to these providers for processing and returned to the App. These
        providers act under their own terms and privacy policies, and we require
        them by contract to handle data appropriately and to use it only for
        permitted purposes.
      </p>
      <p>
        We are not responsible for outages, limitations, or changes to third-
        party services that are outside our reasonable control. Some features
        may be added, removed, or changed in response to third-party platform
        requirements or technical constraints.
      </p>

      <div className={s.title}>12) Accuracy, Reliance, and No Professional Advice</div>
      <p>
        Transcriptions and summaries produced by the App are approximate and may
        contain errors or omissions. They are intended to assist you in
        organizing information and should not be treated as complete records of
        any lecture, meeting, or conversation. The App does not provide legal,
        medical, financial, educational, or other professional advice, and its
        output should not be used as the sole basis for high-stakes decisions.
        You remain responsible for verifying important information against
        original sources or official records.
      </p>

      <div className={s.title}>13) Limitation of Liability</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. To the extent allowed by law, we are not liable for
        indirect, incidental, consequential, or punitive damages, loss of
        profits, data loss (including loss of recordings, transcripts, or
        summaries), or business interruption arising from or related to your use
        of or inability to use the App. Some jurisdictions do not allow certain
        limitations or exclusions, so parts of this section may not apply to
        you.
      </p>

      <div className={s.title}>14) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your content, your breach of these
        Terms, or your violation of any rights of a third party (including
        recording without proper consent where required).
      </p>

      <div className={s.title}>15) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>16) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Note Spark: AI Note Taken, which explains how information is collected,
        used, stored, and shared, including audio recordings and derived text.
        In case of conflict between these Terms and the Privacy Policy regarding
        data processing, the Privacy Policy will guide how we handle personal
        data, while these Terms govern your use of the App and its features. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>17) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including new languages,
        export options, or AI capabilities), or modify these Terms from time to
        time. The “Last updated” date at the top of this document indicates when
        these Terms were last revised. Material changes may be highlighted in
        the App or via other reasonable means. By continuing to use the App
        after changes take effect, you accept the updated Terms. If you do not
        agree to the revised Terms, you must stop using the App and uninstall
        it.
      </p>

      <div className={s.title}>18) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
